import { action, makeAutoObservable, reaction } from "mobx";
import {
  DonationOptionsPageModel,
  DonationPricingPageInputsModel,
} from "../models/DonationAndItemModel";
import RelatedItemsService from "../services/RelatedItemsService";
import BeneficiaryService from "../services/BeneficiaryService";
import { ItemDetailInputModel } from "../models/ItemDetailInputModel";
import { PriceBenefitsValueMappingModel } from "../models/PriceInputs.model";
import { BenefitsModel } from "../models/BenefitsModel";
import {
  IAPICreateUpdateDonationModel,
  IAPIGetEditDonationModel,
} from "../interfaces/IAPIDonationModels";
import { UpsertDonationActiveStep } from "../enums/Donation.enum";
import { AuthService } from "@mindmemobile/mm-shared-global";

export default class DonationStore {
  public headerCaption: string = "Detail";
  public activeStep: UpsertDonationActiveStep = UpsertDonationActiveStep.Detail;
  public pricingPageData: DonationPricingPageInputsModel =
    new DonationPricingPageInputsModel();
  public optionsPage: DonationOptionsPageModel;
  public showAddBenefitModal: boolean = false;
  public relatedItemService: RelatedItemsService;
  public beneficiaryService: BeneficiaryService = new BeneficiaryService();
  public detailPage: ItemDetailInputModel = new ItemDetailInputModel({
    isTaxIdRequired: true,
  });
  public scrollToField: keyof DonationPricingPageInputsModel = "title";
  public isSaveUpdateInProgress: boolean = false;
  constructor() {
    this.optionsPage = new DonationOptionsPageModel();
    this.relatedItemService = new RelatedItemsService();
    makeAutoObservable(this);
    reaction(
      () => this.activeStep,
      () => this.updateStateBasedOnActiveStatusChange(),
    );
  }

  updatePricingPageData(data: DonationPricingPageInputsModel) {
    this.pricingPageData = new DonationPricingPageInputsModel(data);
  }

  updateScrollToField(field: keyof DonationPricingPageInputsModel) {
    this.scrollToField = field;
  }

  updateOptionsPage(data: DonationOptionsPageModel) {
    this.optionsPage = new DonationOptionsPageModel(data);
  }

  updateDetailsPage(data: ItemDetailInputModel) {
    this.detailPage = new ItemDetailInputModel(data);
  }

  updateBenefitMapping(
    benefits: BenefitsModel[],
    existingBenefits: PriceBenefitsValueMappingModel[],
  ) {
    const mappedBenefits: PriceBenefitsValueMappingModel[] = [];
    benefits.forEach((b) => {
      const existingTermBenefits = existingBenefits.find(
        (x) => x.benefitId === b.id,
      );

      mappedBenefits.push(
        new PriceBenefitsValueMappingModel({
          benefitId: b.id,
          benefitName: b.pluralName,
          value: existingTermBenefits?.value || "",
          errors: existingTermBenefits?.errors || [],
        }),
      );
    });

    return mappedBenefits;
  }

  @action
  validate(step?: UpsertDonationActiveStep): boolean {
    let isValid = true;
    const isTabChange = step ? true : false;
    const stepToValidate = isTabChange ? step : this.activeStep;
    switch (stepToValidate) {
      case UpsertDonationActiveStep.Detail:
        isValid = this.detailPage.validate();
        this.detailPage = new ItemDetailInputModel(this.detailPage);
        break;
      case UpsertDonationActiveStep.Pricing:
        isValid = this.pricingPageData.validate();
        this.pricingPageData = new DonationPricingPageInputsModel(
          this.pricingPageData,
        );
        break;
      case UpsertDonationActiveStep.Options:
        isValid = this.optionsPage.validate();
        // if (!this.realtedItemService.relatedSectionData.validate()) {
        //     isValid = false;
        //     this.realtedItemService.relatedSectionData = new RelatedSectionModel({
        //         ...this.realtedItemService.relatedSectionData,
        //     });
        // }
        this.optionsPage = new DonationOptionsPageModel(this.optionsPage);
        break;
    }
    return isValid;
  }

  @action
  validatePreviousSteps(currentStep: UpsertDonationActiveStep): {
    isValid: boolean;
    errorStep?: UpsertDonationActiveStep;
  } {
    const steps = [
      UpsertDonationActiveStep.Detail,
      UpsertDonationActiveStep.Pricing,
      UpsertDonationActiveStep.Options,
    ];
    const currentStepIndex = steps.indexOf(currentStep);
    for (let i = 0; i < currentStepIndex; i++) {
      const step = steps[i];
      if (!this.validate(step)) {
        return { isValid: false, errorStep: step };
      }
    }
    return { isValid: true };
  }

  updateActiveStep(step: UpsertDonationActiveStep) {
    this.activeStep = step;
  }

  updateStateBasedOnActiveStatusChange() {
    switch (this.activeStep) {
      case UpsertDonationActiveStep.Detail:
        this.headerCaption = "Donation Details";
        break;
      case UpsertDonationActiveStep.Pricing:
        this.headerCaption = "Donation Pricing";
        break;
      case UpsertDonationActiveStep.Options:
        this.headerCaption = "Donation Options";
        break;
    }
  }

  reset() {
    this.headerCaption = "Detail";
    this.detailPage = new ItemDetailInputModel({ isTaxIdRequired: true });
    this.activeStep = UpsertDonationActiveStep.Detail;
    this.pricingPageData = new DonationPricingPageInputsModel();
    this.optionsPage = new DonationOptionsPageModel();
    this.relatedItemService = new RelatedItemsService();
    this.isSaveUpdateInProgress = false;
  }

  toggleSaveUpdateInProgress() {
    this.isSaveUpdateInProgress = !this.isSaveUpdateInProgress;
  }

  toggleAddBenefitModal() {
    this.showAddBenefitModal = !this.showAddBenefitModal;
  }

  createAPIModel(): IAPICreateUpdateDonationModel {
    const optionsModel = this.optionsPage.createAPIModel();
    const itemDetail = this.detailPage.createAPIModel();
    return {
      ...itemDetail,
      accountId: AuthService.authData.accountId,
      price: this.pricingPageData.createAPIModel(),
      options: optionsModel,
    } as IAPICreateUpdateDonationModel;
  }

  constructStoreFromAPI(data: IAPIGetEditDonationModel) {
    this.detailPage = ItemDetailInputModel.deserializeAPIModel(data, true);
    if (data.options) {
      this.optionsPage = DonationOptionsPageModel.deserializeAPIModel(
        data.options,
      );
    }
    this.pricingPageData = DonationPricingPageInputsModel.deserializeAPIModel(
      data.price,
    );
  }
}
